import { error } from './../../models/error-models/apiErrorResponse';
import { ChangeDetectorRef, Component, Input, OnInit, ViewChild } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { TabViewModule } from 'primeng/tabview';
import { DialogModule } from 'primeng/dialog';
import { InputTextareaModule } from 'primeng/inputtextarea';
import { MessageService } from 'primeng/api';
import { FileSelectEvent, FileUpload, FileUploadModule } from 'primeng/fileupload';
import { ToastModule } from 'primeng/toast';
import { AdditionalInformationService } from '../../services/additional-information/additional-information.service';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { Store } from '@ngxs/store';
import { UserSelectors } from '../../states/user/user-selector';
import { forkJoin, of, switchMap } from 'rxjs';
import { UserStateModel } from '../../states/user/user-state-model';
import { AdditionalInformation } from '../../models/enums/additionalInformation';
import { GetAdditionalInformationResponse } from '../../models/account/additional-information/getAdditionalInformationResponse';
import { AdditionalInformationResponse } from '../../models/account/additional-information/additionalInformationResponse';
import { CommonModule } from '@angular/common';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
@Component({
  selector: 'app-additional-information',
  standalone: true,
  imports: [ButtonModule, TabViewModule, DialogModule, InputTextareaModule, FileUploadModule, ReactiveFormsModule, ToastModule, ProgressSpinnerModule,
    CommonModule
  ],
  templateUrl: './additional-information.component.html',
  styleUrl: './additional-information.component.scss'
})
export class AdditionalInformationComponent implements OnInit {

  @ViewChild('fileUploader') fileUploader!: FileUpload;

  @Input() infoType!: AdditionalInformation;
  infoStatus: string = 'Pending';
  user!: UserStateModel;
  pendingQuestions: GetAdditionalInformationResponse | undefined;
  resolvedQuestions: GetAdditionalInformationResponse | undefined;
  visible: boolean = false;
  additionalInformationForm!: FormGroup;
  isLoading: boolean = false;
  submitted: boolean = false;
  attachment: File | undefined;
  selectedItemId: number | null = null;
  isLoadingData: boolean = false;
  showDialog(itemid: number) {
    this.selectedItemId = itemid;
    this.visible = true;
  }

  constructor(private messageService: MessageService,
    private additionalInformationService: AdditionalInformationService,
    private formBuilder: FormBuilder,
    private store: Store,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.loadData();
    this.buildForm();
    this.observeAdditionalInfo();
  }

  observeAdditionalInfo() {
    this.additionalInformationService.additonalInfoRequest$.subscribe({
      next: (response) => {
        if (response.type == AdditionalInformation.CheckList || response.type == AdditionalInformation.EssayPilot) {
          this.pendingQuestions!.additionalInfo.unshift(response);
        }
      },
      error: (err) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error in observing additionalInfo' });
      }
    });
  }

  buildForm() {
    this.additionalInformationForm = this.formBuilder.group({
      additionalInfo: ['', Validators.required]
    });
  }

  loadData() {
    this.isLoadingData = true;
    this.store.select(UserSelectors.getUser).pipe(switchMap(user => {
      if (!user || !user.id) {
        return of(null);
      }
      this.user = user;
      return forkJoin({
        pendingQuestions: this.additionalInformationService.getAdditionalInformation({
          studentId: this.user.id,
          type: this.infoType,
          status: 'Pending'
        }),
        resolvedQuestions: this.additionalInformationService.getAdditionalInformation({
          studentId: this.user.id,
          type: this.infoType,
          status: 'Submitted'
        }), // ✅ No empty string
      });
    })).subscribe({
      next: response => {
        if (response != null) {
          this.pendingQuestions = response.pendingQuestions;
          this.resolvedQuestions = response.resolvedQuestions;
        }
        this.isLoadingData = false;
      },
      error: (error) => {
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error fetching additionalInfo' });
        this.isLoadingData = false;
      }
    });
  }

  onReplySubmit() {
    this.isLoading = true;
    this.submitted = false;
    if (this.additionalInformationForm.invalid) {
      this.isLoading = false;
      return;
    }
    this.additionalInformationService.updateAdditionalInformation({
      id: this.selectedItemId!,
      reply: this.additionalInformationForm.value.additionalInfo,
      attachment: this.attachment
    }).subscribe({
      next: (response) => {
        this.isLoading = false;
        this.submitted = false;
        this.additionalInformationForm.reset();
        this.attachment = undefined;
        this.fileUploader.clear();
        this.selectedItemId = null;
        this.closeReply();
        this.moveItemToResolved(response);
        this.messageService.add({ severity: "success", summary: "Success", detail: "Information updated successfully!" });
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Error while updating additionalInfo' });
      }
    })
  }



  moveItemToResolved(response: AdditionalInformationResponse) {
    if (!this.pendingQuestions?.additionalInfo || !this.selectedItemId) return;

    // Ensure resolvedQuestions is initialized
    this.resolvedQuestions = this.resolvedQuestions ?? { additionalInfo: [] };
    this.resolvedQuestions.additionalInfo = this.resolvedQuestions.additionalInfo ?? [];

    // Find and remove item from pendingQuestions
    const itemIndex = this.pendingQuestions.additionalInfo.findIndex(item => item.id === this.selectedItemId);
    if (itemIndex !== -1) {
      const movedItem = this.pendingQuestions.additionalInfo.splice(itemIndex, 1)[0];

      // Add to resolvedQuestions with updated properties
      this.resolvedQuestions.additionalInfo.unshift({
        ...response, // Spread response first to maintain priority
        id: this.selectedItemId,
        attachementSAS: response.attachment, // Ensure correct attachment assignment
      });
    }
  }


  closeReply() {
    this.visible = false;
    this.attachment = undefined;
    this.fileUploader.clear();
    this.selectedItemId = null;
    this.additionalInformationForm.reset();
    this.additionalInformationForm.clearValidators();
  }


  onFileSelect($event: FileSelectEvent): void {
    if ($event.files && $event.files.length > 0) {
      // Convert files to an array
      const filesArray = Array.from($event.files);
      var index = 0;
      filesArray.forEach(item => {
        if (item.size > 1048576) { // 1 MB in bytes
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: `${item.name} file size can't exceed 1 MB`
          });
          filesArray.splice(index, 1);
          this.fileUploader.clear();
        }
        index++;
      });
      if (filesArray.length > 0) {
        this.attachment = filesArray[0];
      }
    }
  }

  openAttachment(fileUrl: string | undefined) {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  }
  onFileRemoved() {
    this.attachment = undefined;
    if (this.fileUploader) {
      this.fileUploader.clear();   // ✅ This clears the UI preview
    }
    this.cdr.detectChanges();      // Force UI refresh
  }
}
